import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReportFilters from '../components/reports/ReportFilters';
import CampaignCompletionReport from '../components/reports/CampaignCompletionReport';
import PhishingSimReport from '../components/reports/PhishingSimReport';
import DepartmentRiskReport from '../components/reports/DepartmentRiskReport';
import ComplianceReport from '../components/reports/ComplianceReport';
import LearnerActivityReport from '../components/reports/LearnerActivityReport';
import OverdueTrainingReport from '../components/reports/OverdueTrainingReport';
import AuditLogReport from '../components/reports/AuditLogReport';
import PageHeader from '../components/ui/PageHeader';
import { dataFetch } from '../api';

const { FiBookOpen, FiTarget, FiShield, FiCheckCircle, FiUsers, FiAlertTriangle, FiLoader, FiList } = FiIcons;

const REPORTS = [
  { id: 'campaign',    label: 'Campaign Completion', icon: FiBookOpen,      color: 'text-blue-600',    bg: 'bg-blue-50'    },
  { id: 'phishing',    label: 'Phishing Sims',       icon: FiTarget,        color: 'text-red-600',     bg: 'bg-red-50'     },
  { id: 'department',  label: 'Department Risk',     icon: FiShield,        color: 'text-violet-600',  bg: 'bg-violet-50'  },
  { id: 'compliance',  label: 'Compliance',          icon: FiCheckCircle,   color: 'text-teal-600',    bg: 'bg-teal-50'    },
  { id: 'learner',     label: 'Learner Activity',    icon: FiUsers,         color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { id: 'overdue',     label: 'Overdue Training',    icon: FiAlertTriangle, color: 'text-orange-600',  bg: 'bg-orange-50'  },
  { id: 'audit',       label: 'Audit Log',           icon: FiList,          color: 'text-indigo-600',  bg: 'bg-indigo-50'  },
];

const currentYear = new Date().getFullYear();
const defaultFilters = { department: 'All', location: 'All', manager: 'All', campaign: 'All', learner: 'All', year: currentYear };

const Reports = () => {
  const [activeReport, setActiveReport] = useState('campaign');
  const [filters, setFilters] = useState(defaultFilters);
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [empRes, campRes, assignRes, phishRes, compRes, auditRes, polAckRes, modAckRes] = await Promise.all([
          dataFetch('read/employees?limit=5000'),
          dataFetch('read/campaigns?limit=5000'),
          dataFetch('read/assignments?limit=5000'), // No year filter to ensure we get all records
          dataFetch('read/phishing_simulations?limit=5000'),
          dataFetch('read/compliance_records?limit=5000'),
          dataFetch('read/audit_logs?limit=5000'),
          dataFetch('read/policy_acknowledgements?limit=5000'),
          dataFetch('read/module_acknowledgements?limit=5000')
        ]);
        
        setRaw({
          employees: empRes?.data ?? [],
          // Filter campaigns client-side so we don't accidentally miss assignments
          campaigns: (campRes?.data ?? []).filter(c => {
            if (!filters.year || filters.year === 'All') return true;
            return new Date(c.startDate).getFullYear() === parseInt(filters.year) || 
                   new Date(c.endDate).getFullYear() === parseInt(filters.year);
          }),
          assignments: assignRes?.data ?? [],
          phishingSimulations: phishRes?.data ?? [],
          complianceRecords: compRes?.data ?? [],
          auditLogs: auditRes?.data ?? [],
          policyAcks: polAckRes?.data ?? [],
          moduleAcks: modAckRes?.data ?? []
        });
      } catch (e) {
        console.error("Failed to fetch report data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters.year]); // Refetch and re-filter when year changes

  const renderReport = () => {
    if (loading || !raw) return null;
    const props = { filters, raw };
    switch (activeReport) {
      case 'campaign':   return <CampaignCompletionReport {...props} />;
      case 'phishing':   return <PhishingSimReport {...props} />;
      case 'department': return <DepartmentRiskReport {...props} />;
      case 'compliance': return <ComplianceReport {...props} />;
      case 'learner':    return <LearnerActivityReport {...props} />;
      case 'overdue':    return <OverdueTrainingReport {...props} />;
      case 'audit':      return <AuditLogReport {...props} />;
      default: return null;
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <PageHeader eyebrow="Analytics" title="Reports" description="Export and analyze training, phishing, and compliance data across your organization." />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-1.5 flex gap-1 flex-wrap">
        {REPORTS.map(r => (
          <button key={r.id} onClick={() => setActiveReport(r.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeReport === r.id ? `${r.bg} ${r.color} shadow-sm` : 'text-slate-600 hover:bg-slate-50'}`}>
            <SafeIcon icon={r.icon} className="text-sm" />{r.label}
          </button>
        ))}
      </div>

      {!loading && raw && (
        <ReportFilters filters={filters} setFilters={setFilters} raw={raw} />
      )}

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div key="loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-20 text-slate-500">
            <SafeIcon icon={FiLoader} className="text-3xl animate-spin mb-4 text-blue-500" />
            <p className="text-sm font-medium">Fetching report data...</p>
          </motion.div>
        ) : (
          <motion.div key={activeReport} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {renderReport()}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Reports;